import { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Star } from '@phosphor-icons/react';
import { useChildTokens } from './useChildTokens';
import { formatPurchaseDate } from './purchaseDisplay';
import type { Purchase } from '@/api/types';

/**
 * PurchaseReceiptDialog — barnets KVITTO på ett köp i stjärnbutiken (SHOP-03/SHOP-06).
 *
 * Vyn har ett enda syfte: den ska kunna hållas upp fysiskt mot en vuxen. Därför stor
 * emoji, stort varunamn, pris och köpdatum — och EN mening riktad till den vuxne
 * ("Jag har köpt den här med mina stjärnor."). Fullskärm på mobil så att inget annat
 * i appen konkurrerar om uppmärksamheten i det ögonblicket.
 *
 * MEDVETNA BEGRÄNSNINGAR:
 * - INGEN knapp här ändrar status. Barnet får aldrig markera sitt eget köp som uthämtat;
 *   inlösen är en föräldrahandling i handledarläget (SupervisorShop). Enda utgången är "Klar".
 * - Statisk vy: ingen konfetti, ingen animation, inget ljud. Jublet hör till köpögonblicket,
 *   inte till kvittot som ska läsas i lugn och ro tillsammans med en vuxen.
 *
 * Varunamn/emoji tas ALLTID från köpets snapshot (`itemNameSnapshot`/`itemEmojiSnapshot`),
 * aldrig från aktuell ShopItem — varan kan vara raderad (`shopItemId` kan vara null).
 */

type PurchaseReceiptDialogProps = {
  open: boolean;
  /** Köpet som visas. Får vara null när dialogen är stängd. */
  purchase: Purchase | null;
  onClose: () => void;
};

const PurchaseReceiptDialog = ({ open, purchase, onClose }: PurchaseReceiptDialogProps) => {
  const { t, i18n } = useTranslation();
  const tokens = useChildTokens();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // MUI håller innehållet monterat under stängningsanimationen. Utan detta blinkar
  // kvittot tomt när förälderns/barnets klick nollar `purchase` samtidigt som dialogen
  // fadar ut. Vi visar därför kvar det senast visade köpet under uttoningen.
  const lastShown = useRef<Purchase | null>(null);
  useEffect(() => {
    if (purchase) lastShown.current = purchase;
  }, [purchase]);

  const shown = purchase ?? lastShown.current;
  const boughtOn = shown ? formatPurchaseDate(shown.createdAt, i18n.language) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
      // Rubriken renderas bara när det finns ett köp att visa. Utan villkoret pekar
      // aria-labelledby på ett id som inte finns (dialogen står då utan tillgängligt
      // namn); i det läget namnger vi dialogen direkt i stället.
      aria-labelledby={shown ? 'purchase-receipt-heading' : undefined}
      aria-label={shown ? undefined : (t('child.shop.purchases.receiptHeading') as string)}
      PaperProps={{
        sx: {
          bgcolor: tokens.card,
          borderRadius: fullScreen ? 0 : '20px',
        },
      }}
    >
      <DialogContent>
        {shown && (
          <Stack spacing={1.5} alignItems="center" textAlign="center" py={2}>
            <Typography
              id="purchase-receipt-heading"
              sx={{ fontWeight: 800, fontSize: '1rem', color: tokens.textSoft }}
            >
              {t('child.shop.purchases.receiptHeading')}
            </Typography>

            <Box aria-hidden sx={{ fontSize: '4.5rem', lineHeight: 1.1 }}>
              {shown.itemEmojiSnapshot ?? '⭐'}
            </Box>

            <Typography sx={{ fontWeight: 900, fontSize: '1.6rem', color: tokens.text }}>
              {shown.itemNameSnapshot}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Star size={22} weight="fill" color={tokens.orange} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: tokens.text }}>
                {shown.priceStars}
              </Typography>
            </Stack>

            {boughtOn !== null && (
              <Typography sx={{ fontSize: '1rem', color: tokens.textSoft }}>
                {t('child.shop.purchases.boughtOn', { date: boughtOn })}
              </Typography>
            )}

            {/* Meningen som gör kvittot begripligt för den vuxne som får det uppvisat. */}
            <Box
              sx={{
                mt: 1,
                width: '100%',
                bgcolor: tokens.primaryLight,
                border: '1px solid',
                borderColor: tokens.border,
                borderRadius: '16px',
                p: 1.75,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: tokens.text }}>
                {t('child.shop.purchases.receiptForAdult')}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          autoFocus
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderRadius: '999px',
            px: 4,
            py: 1,
            fontSize: '1rem',
          }}
        >
          {t('child.shop.purchases.receiptClose')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseReceiptDialog;
